import {
    AlertCircle,
    ArrowRight,
    BadgeCheck,
    Building2,
    Check,
    CheckCircle2,
    CreditCard,
    Eye,
    EyeOff,
    FileCheck2,
    Lock,
    Save,
    ShieldCheck,
    UserRound,
    Wallet,
    X,
} from "lucide-react";
import {
    FormEvent,
    useEffect,
    useMemo,
    useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";

import { AppDispatch, RootState } from "../../../../store";
import {
    getGlobalKycDetails,
    getKycDetails,
    getKycDraft,
    kycCheckout,
    refreshKycPaymentStatus,
    saveKycDraft,
    submitGlobalCustomerKyc,
    submitUnifiedKycWithVerification,
    uploadKycDocument,
} from "../../../../Redux/customer_kyc/slice";
import { startCashfreeCheckout } from "../../../../utility/cashfreeCheckout";

import KycImageUpload from "./KycImageUpload";
import KycSelect from "./KycSelect";
import {
    accountTypeOptions,
    bankOptionsByCountry,
    countryOptions,
    fromCanonicalIdType,
    getIdNumberPlaceholder,
    GLOBAL_ID_OPTIONS,
    kycIdOptions,
    toCanonicalIdType,
    toGlobalDocType,
} from "./KycPageData";

import "./KycPage.css";

type KycForm = {
    idType: string;
    idNumber: string;
    frontImage: string;
    frontImageName: string;
    backImage: string;
    backImageName: string;
    remarks: string;

    country: string;
    bank: string;
    accountHolderName: string;
    accountType: string;
    accountNumber: string;
    ifscCode: string;
    branchName: string;
    panNumber: string;

    nomineeName: string;
    nomineeMobile: string;
    nomineeRelation: string;
    nomineeEmail: string;
    transactionPassword: string;
};

const initialForm: KycForm = {
    idType: "",
    idNumber: "",
    frontImage: "",
    frontImageName: "",
    backImage: "",
    backImageName: "",
    remarks: "",

    country: "India",
    bank: "",
    accountHolderName: "",
    accountType: "Savings",
    accountNumber: "",
    ifscCode: "",
    branchName: "",
    panNumber: "",

    nomineeName: "",
    nomineeMobile: "",
    nomineeRelation: "",
    nomineeEmail: "",
    transactionPassword: "",
};

const KycPage = () => {
    const dispatch = useDispatch<AppDispatch>();
    const [searchParams, setSearchParams] = useSearchParams();
    const paymentId = searchParams.get("paymentId");

    const { kycData, globalKycData, kycDraft, loading, error: reduxError } = useSelector(
        (state: RootState) => state.customerKyc
    );
    const { user } = useSelector((state: RootState) => state.customerAuth);

    const [form, setForm] = useState<KycForm>(initialForm);
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [submitted, setSubmitted] = useState(false);
    const [draftSaved, setDraftSaved] = useState(false);
    const [statusMessage, setStatusMessage] = useState<string | null>(null);

    // Upload states
    const [uploadingFront, setUploadingFront] = useState(false);
    const [uploadingBack, setUploadingBack] = useState(false);

    // Password visibility toggle
    const [showPassword, setShowPassword] = useState(false);

    // Modal states
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [showResultModal, setShowResultModal] = useState(false);
    const [loadingChargeInfo, setLoadingChargeInfo] = useState(false);
    const [checkoutDetails, setCheckoutDetails] = useState<any>(null);
    const [verifyingState, setVerifyingState] = useState(false);
    const [validatedData, setValidatedData] = useState<any>(null);
    const [paymentError, setPaymentError] = useState<string | null>(null);

    // Country & region check
    const userCountry = (user?.country || "").trim().toLowerCase();
    const isIndia = useMemo(() => {
        if (form.country) {
            const c = form.country.trim().toLowerCase();
            return c === "india" || c === "in";
        }
        return userCountry === "india" || userCountry === "in" || userCountry === "";
    }, [form.country, userCountry]);

    const activeKycData = isIndia ? kycData : globalKycData;

    // Password validation rule
    const validatePassword = (password: string) => {
        if (!password) return "Transaction password is required";
        if (password.length < 6 || password.length > 15) return "Password must be 6-15 characters";
        if (!/[A-Z]/.test(password)) return "Must contain at least one uppercase letter";
        if (!/[a-z]/.test(password)) return "Must contain at least one lowercase letter";
        if (!/[0-9]/.test(password)) return "Must contain at least one number";
        if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) return "Must contain at least one special character";
        return "";
    };

    // Initial Fetch
    useEffect(() => {
        if (isIndia) {
            dispatch(getKycDetails());
        } else {
            dispatch(getGlobalKycDetails());
        }
        dispatch(getKycDraft());
    }, [dispatch, isIndia]);

    // Handle Cashfree return landing on #payment-<status> hash
    // (payment service redirects KYC payments back here with a hash fragment)
    useEffect(() => {
        const hash = window.location.hash;
        if (!hash.startsWith("#payment-")) return;

        const status = hash.replace("#payment-", "").toUpperCase();
        window.history.replaceState(
            null,
            "",
            window.location.pathname + window.location.search
        );

        // When paymentId is also present, the refresh effect owns messaging/refetch
        if (paymentId) return;

        if (status === "SUCCESS" || status === "COMPLETED") {
            toast.success("Payment successful! Your KYC has been submitted.");
            if (isIndia) dispatch(getKycDetails());
            else dispatch(getGlobalKycDetails());
        } else if (status === "FAILED" || status === "ERROR") {
            toast.error("Payment failed or was not completed. Please try again.");
        } else {
            toast.info("Payment is pending. Your KYC will be updated once confirmed.");
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Refresh payment status if redirected back with paymentId
    useEffect(() => {
        if (paymentId) {
            toast.info(`Checking payment status for payment: ${paymentId}...`);
            dispatch(refreshKycPaymentStatus(paymentId)).then((res: any) => {
                if (refreshKycPaymentStatus.fulfilled.match(res)) {
                    const status = res.payload?.data?.status;
                    if (status === "Success") {
                        toast.success("Payment verified successfully!");
                    } else {
                        toast.info(`Payment status: ${status || "Pending"}`);
                    }
                    if (isIndia) dispatch(getKycDetails());
                    else dispatch(getGlobalKycDetails());
                } else {
                    toast.error("Payment status update failed.");
                }
                setSearchParams({});
            });
        }
    }, [paymentId, dispatch, setSearchParams, isIndia]);

    // Pre-populate data from Redux
    useEffect(() => {
        if (!activeKycData) return;

        if (activeKycData.kyc) {
            const k = activeKycData.kyc as any;
            const docType = k.documentType || "";
            setForm((prev) => ({
                ...prev,
                idType: fromCanonicalIdType(docType),
                idNumber: k.idNumber || "",
                frontImage: k.fileRef || k.frontImage || "",
                frontImageName: k.fileRef || k.frontImage ? "ID Front Image" : "",
                backImage: k.backImageRef || k.backImage || "",
                backImageName: k.backImageRef || k.backImage ? "ID Back Image" : "",
                remarks: k.remarks || "",
            }));
        }

        if (activeKycData.bank) {
            const b = activeKycData.bank as any;
            setForm((prev) => ({
                ...prev,
                country: b.country || user?.country || (isIndia ? "India" : ""),
                bank: b.bankName || "",
                accountHolderName: b.accountHolderName || "",
                accountType: b.accountType || "Savings",
                accountNumber: String(b.accountNumber || b.accountNumberOrIban || ""),
                ifscCode: b.ifscCode || b.swiftOrBicCode || b.swiftCode || "",
                branchName: b.branchName || b.bankAddress || "",
                panNumber: String(b.panCardNo || ""),
            }));
        } else if (user?.country) {
            setForm((prev) => ({ ...prev, country: user.country || (isIndia ? "India" : "") }));
        }

        if (activeKycData.nominee) {
            const n = activeKycData.nominee as any;
            setForm((prev) => ({
                ...prev,
                nomineeName: n.nomineeName || "",
                nomineeMobile: n.nomineeMobile || "",
                nomineeRelation: n.nomineeRelation || "",
                nomineeEmail: n.nomineeEmail || "",
            }));
        }
    }, [activeKycData, user, isIndia]);

    // Restore saved draft when no submitted KYC/bank/nominee data exists
    useEffect(() => {
        if (!kycDraft) return;
        const hasSubmitted = !!(
            activeKycData?.kyc ||
            activeKycData?.bank ||
            activeKycData?.nominee
        );
        if (hasSubmitted) return;

        const d = kycDraft as any;
        setForm((prev) => ({
            ...prev,
            idType: d.documentType ? fromCanonicalIdType(d.documentType) : prev.idType,
            idNumber: d.idNumber || prev.idNumber,
            frontImage: d.frontImage || prev.frontImage,
            frontImageName: d.frontImage ? "ID Front Image" : prev.frontImageName,
            backImage: d.backImage || prev.backImage,
            backImageName: d.backImage ? "ID Back Image" : prev.backImageName,
            remarks: d.remarks || prev.remarks,
            country: d.country || prev.country,
            bank: d.bankName || prev.bank,
            accountHolderName: d.accountHolderName || prev.accountHolderName,
            accountType: d.accountType || prev.accountType,
            accountNumber: String(d.accountNumber || d.accountNumberOrIban || prev.accountNumber),
            ifscCode: d.ifscCode || d.swiftOrBicCode || prev.ifscCode,
            branchName: d.branchName || d.bankAddress || prev.branchName,
            panNumber: d.panCardNo || prev.panNumber,
            nomineeName: d.nomineeName || prev.nomineeName,
            nomineeMobile: d.nomineeMobile || prev.nomineeMobile,
            nomineeRelation: d.nomineeRelation || prev.nomineeRelation,
            nomineeEmail: d.nomineeEmail || prev.nomineeEmail,
        }));
        setDraftSaved(true);
    }, [kycDraft, activeKycData]);

    // Available banks for current country
    const bankOptions = useMemo(() => {
        const countryKey = (form.country || "india").toLowerCase().replace(/\s+/g, "-");
        return bankOptionsByCountry[countryKey] || bankOptionsByCountry[form.country] || bankOptionsByCountry.india || [];
    }, [form.country]);

    // Validation helper
    const validateField = (name: string, value: string) => {
        let error = "";
        const v = value ? value.trim() : "";

        switch (name) {
            case "idType":
                if (!v) error = "ID Type is required";
                break;

            case "idNumber":
                if (!v) {
                    error = "ID Number is required";
                    break;
                }
                const upperVal = v.toUpperCase();
                const canonicalId = toCanonicalIdType(form.idType);
                if (canonicalId === "Aadhar Card") {
                    if (!/^\d{12}$/.test(v)) error = "Aadhaar must be exactly 12 digits";
                } else if (canonicalId === "Pan Card") {
                    if (!/^[A-Z]{5}\d{4}[A-Z]$/.test(upperVal)) error = "Invalid PAN (e.g. ABCDE1234F)";
                } else if (canonicalId === "Passport") {
                    if (!/^[A-Z][1-9][0-9]\s?[0-9]{4}[1-9]$/.test(upperVal) && upperVal.length < 6) {
                        error = "Invalid Passport format (e.g. A1234561)";
                    }
                } else if (canonicalId === "ssn") {
                    if (!/^\d{9}$/.test(v)) error = "SSN must be 9 digits";
                } else if (canonicalId === "Voter Id") {
                    if (!/^[A-Z0-9]{1,10}$/.test(upperVal)) error = "Voter ID must be alphanumeric, max 10 chars";
                } else if (canonicalId === "Driving License") {
                    if (v.length < 6) error = "Driving License must be at least 6 characters";
                }
                break;

            case "accountHolderName":
                if (!v) error = "Account Holder Name is required";
                else if (v.length > 40) error = "Max 40 characters allowed";
                break;

            case "bank":
                if (!v) error = "Bank is required";
                break;

            case "accountType":
                if (!v) error = "Account Type is required";
                break;

            case "accountNumber":
                if (!v) error = "Account Number is required";
                else if (isIndia && (!/^\d+$/.test(v) || v.length < 9 || v.length > 18)) {
                    error = "Must be 9–18 digits";
                } else if (!isIndia && !/^[a-zA-Z0-9]{8,34}$/.test(v)) {
                    error = "Must be 8-34 alphanumeric characters";
                }
                break;

            case "ifscCode":
                if (!v) error = isIndia ? "IFSC Code is required" : "SWIFT / Routing Code is required";
                else if (isIndia && v.length !== 11) error = "Must be exactly 11 characters";
                else if (!isIndia && v.length < 6) error = "Must be at least 6 characters";
                break;

            case "branchName":
                if (!v) error = "Branch Name is required";
                else if (v.length < 4) error = "Min 4 characters required";
                break;

            case "panNumber":
                if (v && !/^[A-Z]{5}\d{4}[A-Z]$/.test(v.toUpperCase())) {
                    error = "Invalid PAN format (e.g. ABCDE1234F)";
                }
                break;

            case "nomineeName":
                if (v && v.length > 40) error = "Max 40 characters allowed";
                break;

            case "nomineeMobile":
                if (v && !/^\d{10,15}$/.test(v)) error = "Must be 10-15 digits";
                break;

            case "nomineeRelation":
                if (v && !/^[A-Za-z\s]+$/.test(v)) error = "Alphabets only";
                break;

            case "nomineeEmail":
                if (v && !/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(v)) {
                    error = "Invalid email format";
                }
                break;

            case "transactionPassword":
                error = validatePassword(value);
                break;
        }

        return error;
    };

    const updateField = <K extends keyof KycForm>(key: K, value: KycForm[K]) => {
        let sanitizedValue = value;

        // Auto formatting based on field
        if (key === "idNumber") {
            const canonical = toCanonicalIdType(form.idType);
            if (canonical === "Aadhar Card" || canonical === "ssn") {
                sanitizedValue = (value as string).replace(/\D/g, "").slice(0, canonical === "ssn" ? 9 : 12) as KycForm[K];
            } else if (canonical === "Pan Card") {
                sanitizedValue = (value as string).replace(/[^a-zA-Z0-9]/g, "").toUpperCase().slice(0, 10) as KycForm[K];
            } else if (canonical === "Passport") {
                sanitizedValue = (value as string).replace(/[^a-zA-Z0-9\s]/g, "").toUpperCase().slice(0, 12) as KycForm[K];
            }
        } else if (key === "accountNumber") {
            if (isIndia) {
                sanitizedValue = (value as string).replace(/\D/g, "").slice(0, 18) as KycForm[K];
            } else {
                sanitizedValue = (value as string).replace(/[^a-zA-Z0-9]/g, "").slice(0, 34) as KycForm[K];
            }
        } else if (key === "ifscCode") {
            sanitizedValue = (value as string).replace(/[^a-zA-Z0-9]/g, "").toUpperCase().slice(0, 11) as KycForm[K];
        } else if (key === "panNumber") {
            sanitizedValue = (value as string).replace(/[^a-zA-Z0-9]/g, "").toUpperCase().slice(0, 10) as KycForm[K];
        } else if (key === "accountHolderName" || key === "nomineeName") {
            sanitizedValue = (value as string).replace(/[^a-zA-Z\s]/g, "").replace(/^\s+/, "").slice(0, 40) as KycForm[K];
        } else if (key === "nomineeRelation") {
            sanitizedValue = (value as string).replace(/[^a-zA-Z\s]/g, "").replace(/^\s+/, "").slice(0, 30) as KycForm[K];
        } else if (key === "nomineeMobile") {
            sanitizedValue = (value as string).replace(/\D/g, "").slice(0, 15) as KycForm[K];
        }

        setForm((prev) => ({
            ...prev,
            [key]: sanitizedValue,
        }));

        setErrors((prev) => ({
            ...prev,
            [key]: validateField(key, sanitizedValue as string),
        }));

        setSubmitted(false);
        setDraftSaved(false);
        setStatusMessage(null);
    };

    const handleCountryChange = (country: string) => {
        setForm((prev) => ({
            ...prev,
            country,
            bank: "",
        }));
        setErrors((prev) => ({ ...prev, country: "", bank: "" }));
        setSubmitted(false);
        setDraftSaved(false);
        setStatusMessage(null);
    };

    // Document image upload handler
    const handleFileUpload = async (file: File, type: "front" | "back") => {
        if (!form.idType) {
            toast.warning("Please select ID Type first.");
            return;
        }
        if (!form.idNumber) {
            toast.warning("Please enter ID Number first.");
            return;
        }
        if (file.size > 5 * 1024 * 1024) {
            toast.warning("File size exceeds 5MB limit.");
            return;
        }

        const canonicalType = toCanonicalIdType(form.idType);
        if (type === "front") setUploadingFront(true);
        else setUploadingBack(true);

        try {
            const res = await dispatch(
                uploadKycDocument({
                    file,
                    documentType: canonicalType,
                    documentNumber: form.idNumber,
                })
            ).unwrap();

            const fileUrl = res?.fileUrl || "";
            const fileName = res?.fileName || file.name;

            if (type === "front") {
                setForm((prev) => ({
                    ...prev,
                    frontImage: fileUrl,
                    frontImageName: fileName,
                }));
                setErrors((prev) => ({ ...prev, frontImage: "" }));
            } else {
                setForm((prev) => ({
                    ...prev,
                    backImage: fileUrl,
                    backImageName: fileName,
                }));
                setErrors((prev) => ({ ...prev, backImage: "" }));
            }

            toast.success(`${type === "front" ? "Front" : "Back"} image uploaded successfully.`);
        } catch (err: any) {
            toast.error(err || `Failed to upload ${type} image.`);
        } finally {
            if (type === "front") setUploadingFront(false);
            else setUploadingBack(false);
        }
    };

    // Draft flow: saves current details without initiating paid verification
    const handleSaveDraft = async () => {
        const payload: any = {
            documentType: isIndia ? toCanonicalIdType(form.idType) : toGlobalDocType(form.idType),
            idNumber: form.idNumber.toUpperCase(),
            frontImage: form.frontImage,
            backImage: form.backImage,
            dob: user?.dob ? user.dob.split("T")[0] : undefined,
            remarks: form.remarks,
            country: form.country,
            bankName: form.bank,
            accountHolderName: form.accountHolderName,
            accountType: form.accountType,
            accountNumber: form.accountNumber,
            ifscCode: form.ifscCode.toUpperCase(),
            branchName: form.branchName,
            panCardNo: form.panNumber.toUpperCase(),
            accountNumberOrIban: form.accountNumber,
            swiftOrBicCode: form.ifscCode.toUpperCase(),
            bankAddress: form.branchName,
            currency: isIndia ? "INR" : "USD",
            nomineeName: form.nomineeName,
            nomineeMobile: form.nomineeMobile,
            nomineeRelation: form.nomineeRelation,
            nomineeEmail: form.nomineeEmail,
            region: isIndia ? "local" : "global",
        };

        try {
            await dispatch(saveKycDraft(payload)).unwrap();
            setDraftSaved(true);
            setSubmitted(false);
            toast.success("Draft saved successfully.");
        } catch (err: any) {
            toast.error(err || "Failed to save draft.");
        }
    };

    // Validate form prior to verification checkout
    const validateBeforeSubmit = () => {
        const newErrors: Record<string, string> = {
            idType: validateField("idType", form.idType),
            idNumber: validateField("idNumber", form.idNumber),
            accountHolderName: validateField("accountHolderName", form.accountHolderName),
            bank: validateField("bank", form.bank),
            accountType: validateField("accountType", form.accountType),
            accountNumber: validateField("accountNumber", form.accountNumber),
            ifscCode: validateField("ifscCode", form.ifscCode),
            branchName: validateField("branchName", form.branchName),
            transactionPassword: validateField("transactionPassword", form.transactionPassword),
        };

        if (!form.frontImage) newErrors.frontImage = "Front image is required";
        if (!form.backImage) newErrors.backImage = "Back image is required";

        if (form.panNumber) newErrors.panNumber = validateField("panNumber", form.panNumber);
        if (form.nomineeName) newErrors.nomineeName = validateField("nomineeName", form.nomineeName);
        if (form.nomineeMobile) newErrors.nomineeMobile = validateField("nomineeMobile", form.nomineeMobile);
        if (form.nomineeRelation) newErrors.nomineeRelation = validateField("nomineeRelation", form.nomineeRelation);
        if (form.nomineeEmail) newErrors.nomineeEmail = validateField("nomineeEmail", form.nomineeEmail);

        const filtered = Object.fromEntries(Object.entries(newErrors).filter(([_, v]) => v));
        setErrors(filtered);

        if (Object.keys(filtered).length > 0) {
            const firstKey = Object.keys(filtered)[0];
            toast.warning(filtered[firstKey] || "Please fill in all required fields properly.");
            return false;
        }

        return true;
    };

    // Submit handler: opens verification preview & checkout modal
    const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        if (!validateBeforeSubmit()) {
            return;
        }

        const payload: any = {
            documentType: isIndia ? toCanonicalIdType(form.idType) : toGlobalDocType(form.idType),
            idNumber: String(form.idNumber || "").toUpperCase(),
            frontImage: form.frontImage,
            backImage: form.backImage,
            dob: user?.dob ? user.dob.split("T")[0] : undefined,
            remarks: form.remarks,
            country: form.country,
            bankName: form.bank,
            accountHolderName: form.accountHolderName,
            accountType: form.accountType,
            accountNumber: String(form.accountNumber || ""),
            ifscCode: String(form.ifscCode || "").toUpperCase(),
            branchName: form.branchName,
            panCardNo: String(form.panNumber || "").toUpperCase(),
            accountNumberOrIban: String(form.accountNumber || ""),
            swiftOrBicCode: String(form.ifscCode || "").toUpperCase(),
            bankAddress: form.branchName,
            currency: isIndia ? "INR" : "USD",
            nomineeName: form.nomineeName,
            nomineeMobile: form.nomineeMobile,
            nomineeRelation: form.nomineeRelation,
            nomineeEmail: form.nomineeEmail,
            region: isIndia ? "local" : "global",
        };

        setValidatedData(payload);
        setLoadingChargeInfo(true);
        setShowConfirmModal(true);
        setPaymentError(null);

        try {
            const res = await dispatch(kycCheckout(payload));
            if (kycCheckout.fulfilled.match(res)) {
                const data = res.payload?.data || res.payload;
                setCheckoutDetails(data);
            } else {
                const errMsg = (res.payload as string) || "Failed to calculate checkout details";
                setPaymentError(errMsg);
                toast.error(errMsg);
            }
        } catch (err: any) {
            const errMsg = err?.message || "Failed to preview verification details";
            setPaymentError(errMsg);
            toast.error(errMsg);
        } finally {
            setLoadingChargeInfo(false);
        }
    };

    // Final verification submission from checkout modal
    const confirmAndSubmitVerification = async () => {
        if (!validatedData) return;

        const passErr = validatePassword(form.transactionPassword);
        if (passErr) {
            setErrors((prev) => ({ ...prev, transactionPassword: passErr }));
            toast.warning(passErr);
            return;
        }

        setPaymentError(null);
        setVerifyingState(true);

        const submitPayload = {
            ...validatedData,
            transactionPassword: form.transactionPassword,
        };

        try {
            const action = isIndia
                ? submitUnifiedKycWithVerification(submitPayload)
                : submitGlobalCustomerKyc(submitPayload);

            const res = await dispatch(action);

            setVerifyingState(false);

            if (submitUnifiedKycWithVerification.fulfilled.match(res) || submitGlobalCustomerKyc.fulfilled.match(res)) {
                const payload = res.payload as any;

                if (payload?.paymentStatus === "Failed" || payload?.paymentStatus === "FAILED") {
                    const errMsg = payload?.message || "Payment processing failed. Please try again.";
                    setPaymentError(errMsg);
                    toast.error(errMsg);
                    return;
                }

                const sessionId =
                    payload?.paymentSessionId ||
                    payload?.data?.paymentSessionId ||
                    payload?.payment_session_id ||
                    payload?.data?.payment_session_id;

                const paymentMode = payload?.paymentMode || payload?.data?.paymentMode;

                if (sessionId) {
                    try {
                        setShowConfirmModal(false);
                        await startCashfreeCheckout(sessionId, paymentMode);
                    } catch (err: any) {
                        const sdkErr = err?.message || "Failed to open Cashfree checkout";
                        setPaymentError(sdkErr);
                        toast.error(sdkErr);
                        if (isIndia) dispatch(getKycDetails());
                        else dispatch(getGlobalKycDetails());
                    }
                    return;
                }

                setShowConfirmModal(false);
                setSubmitted(true);
                toast.success("KYC details submitted successfully for verification.");
                if (isIndia) dispatch(getKycDetails());
                else dispatch(getGlobalKycDetails());
            } else {
                const errMsg = (res.payload as string) || "Submission failed. Please check details and try again.";
                setPaymentError(errMsg);
                toast.error(errMsg);
            }
        } catch (err: any) {
            setVerifyingState(false);
            const errMsg = err?.message || "Verification request failed";
            setPaymentError(errMsg);
            toast.error(errMsg);
        }
    };

    // KYC Status & Verdict determination
    const rawStatus = String(activeKycData?.kyc?.status || "Pending").toLowerCase();
    const isVerified = rawStatus === "verified";
    const isRejected = rawStatus === "rejected";
    const statusText = isVerified
        ? "Verified"
        : isRejected
        ? "Rejected"
        : "Verification Pending";

    const statusBadgeClass = isVerified
        ? "verified"
        : isRejected
        ? "rejected"
        : "pending";

    // Verification Result Modal content
    const renderVerificationResultModal = () => {
        if (!showResultModal) return null;

        const kyc = activeKycData?.kyc as any;
        const bank = activeKycData?.bank as any;

        const docType = kyc?.documentType || "KYC Document";
        const docStatus = String(kyc?.status || "Pending");
        const docVerdict = kyc?.verdict || (docStatus.toLowerCase() === "verified" ? "VERIFIED" : docStatus.toLowerCase() === "rejected" ? "REJECTED" : "PENDING_REVIEW");
        const docRemarks = kyc?.remarks || (docVerdict === "VERIFIED" ? "Document verified successfully." : "Under verification or review.");

        const bankStatus = String(bank?.status || "Pending");
        const bankVerdict = bank?.verdict || (bankStatus.toLowerCase() === "verified" ? "VERIFIED" : bankStatus.toLowerCase() === "rejected" ? "REJECTED" : "PENDING_REVIEW");
        const bankRemarks = bank?.remarks || (bankVerdict === "VERIFIED" ? "Bank account verified successfully." : "Under verification or review.");

        return (
            <div className="user-kyc-modal-overlay" onClick={() => setShowResultModal(false)}>
                <div className="user-kyc-modal-box" onClick={(e) => e.stopPropagation()}>
                    <div className="user-kyc-modal-header">
                        <div>
                            <h3>Verification Details</h3>
                            <p>Current automated status for submitted documents and bank account.</p>
                        </div>
                        <button
                            type="button"
                            className="user-kyc-modal-close"
                            onClick={() => setShowResultModal(false)}
                            aria-label="Close"
                        >
                            <X size={17} />
                        </button>
                    </div>

                    <div className="user-kyc-modal-body">
                        {/* Document Verification Card */}
                        <div className="user-kyc-modal-card">
                            <div className="user-kyc-modal-row" style={{ borderBottom: "1px solid #e2e8f0", paddingBottom: "8px" }}>
                                <strong style={{ fontSize: "13px", color: "#1e293b" }}>{docType}</strong>
                                <span
                                    className={`badge ${
                                        docVerdict === "VERIFIED"
                                            ? "text-success"
                                            : docVerdict === "REJECTED"
                                            ? "text-danger"
                                            : "text-warning"
                                    }`}
                                    style={{ fontWeight: 800, fontSize: "11px" }}
                                >
                                    {docVerdict}
                                </span>
                            </div>
                            <div className="user-kyc-modal-row">
                                <span>ID Number</span>
                                <strong>{kyc?.idNumber || "N/A"}</strong>
                            </div>
                            <div className="user-kyc-modal-row">
                                <span>Status Message</span>
                                <span style={{ color: "#334155", textAlign: "right" }}>{docRemarks}</span>
                            </div>
                        </div>

                        {/* Bank Verification Card */}
                        <div className="user-kyc-modal-card">
                            <div className="user-kyc-modal-row" style={{ borderBottom: "1px solid #e2e8f0", paddingBottom: "8px" }}>
                                <strong style={{ fontSize: "13px", color: "#1e293b" }}>Bank Account</strong>
                                <span
                                    className={`badge ${
                                        bankVerdict === "VERIFIED"
                                            ? "text-success"
                                            : bankVerdict === "REJECTED"
                                            ? "text-danger"
                                            : "text-warning"
                                    }`}
                                    style={{ fontWeight: 800, fontSize: "11px" }}
                                >
                                    {bankVerdict}
                                </span>
                            </div>
                            <div className="user-kyc-modal-row">
                                <span>Bank Name</span>
                                <strong>{bank?.bankName || "N/A"}</strong>
                            </div>
                            <div className="user-kyc-modal-row">
                                <span>Account Holder</span>
                                <strong>{bank?.accountHolderName || "N/A"}</strong>
                            </div>
                            <div className="user-kyc-modal-row">
                                <span>Account / IFSC</span>
                                <strong>
                                    {bank?.accountNumber ? `••••${String(bank.accountNumber).slice(-4)}` : "N/A"}{" "}
                                    {bank?.ifscCode ? `(${bank.ifscCode})` : ""}
                                </strong>
                            </div>
                            <div className="user-kyc-modal-row">
                                <span>Status Message</span>
                                <span style={{ color: "#334155", textAlign: "right" }}>{bankRemarks}</span>
                            </div>
                        </div>
                    </div>

                    <div className="user-kyc-modal-footer">
                        <button
                            type="button"
                            className="user-kyc-draft-button"
                            onClick={() => setShowResultModal(false)}
                        >
                            Close
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    // Checkout Confirmation Modal
    const renderCheckoutModal = () => {
        if (!showConfirmModal) return null;

        const docCharge = checkoutDetails?.kycDocument?.charge?.finalPrice ?? 0;
        const bankCharge = checkoutDetails?.bankDetails?.charge?.finalPrice ?? 0;
        const totalCharge =
            typeof checkoutDetails?.paymentDetails?.totalCharge === "number"
                ? checkoutDetails.paymentDetails.totalCharge
                : docCharge + bankCharge;

        const isFirstTime = Boolean(
            checkoutDetails?.summary?.isFirstTimeKyc ||
            checkoutDetails?.paymentDetails?.isFirstTimeKyc ||
            checkoutDetails?.isFirstTimeKyc
        );

        return (
            <div className="user-kyc-modal-overlay" onClick={() => !verifyingState && setShowConfirmModal(false)}>
                <div className="user-kyc-modal-box" onClick={(e) => e.stopPropagation()}>
                    <div className="user-kyc-modal-header">
                        <div>
                            <h3>Verification Checkout</h3>
                            <p>Review charges and confirm verification request.</p>
                        </div>
                        <button
                            type="button"
                            className="user-kyc-modal-close"
                            disabled={verifyingState}
                            onClick={() => setShowConfirmModal(false)}
                            aria-label="Close"
                        >
                            <X size={17} />
                        </button>
                    </div>

                    <div className="user-kyc-modal-body">
                        {loadingChargeInfo ? (
                            <div style={{ textAlign: "center", padding: "30px 0" }}>
                                <div className="spinner-border text-primary" role="status"></div>
                                <p style={{ marginTop: "12px", color: "#64748b", fontSize: "12px", fontWeight: 600 }}>
                                    Calculating verification details...
                                </p>
                            </div>
                        ) : (
                            <>
                                {paymentError && (
                                    <div className="user-kyc-message error" style={{ marginBottom: "14px" }}>
                                        <AlertCircle size={16} />
                                        {paymentError}
                                    </div>
                                )}

                                {/* Action items */}
                                <div className="user-kyc-modal-card">
                                    <div className="user-kyc-modal-row">
                                        <span>Document Action</span>
                                        <strong style={{ color: "#1d4ed8" }}>
                                            {checkoutDetails?.kycDocument?.action || "VERIFY"}
                                        </strong>
                                    </div>
                                    <div className="user-kyc-modal-row">
                                        <span>Bank Action</span>
                                        <strong style={{ color: "#1d4ed8" }}>
                                            {checkoutDetails?.bankDetails?.action || "VERIFY"}
                                        </strong>
                                    </div>
                                </div>

                                {/* Payment Summary */}
                                <div className="user-kyc-modal-card">
                                    <div className="user-kyc-modal-row">
                                        <span>Document Charge</span>
                                        <strong>₹{docCharge}</strong>
                                    </div>
                                    <div className="user-kyc-modal-row">
                                        <span>Bank Verification Charge</span>
                                        <strong>₹{bankCharge}</strong>
                                    </div>
                                    <div className="user-kyc-modal-total-row">
                                        <span>Total Amount</span>
                                        <strong>₹{Number(totalCharge).toFixed(2)}</strong>
                                    </div>
                                </div>

                                {/* Payment method info */}
                                <div className="user-kyc-modal-card" style={{ background: "#f0fdf4", borderColor: "#bbf7d0" }}>
                                    <div style={{ display: "flex", alignItems: "center", gap: "8px", color: "#166534", fontSize: "12px", fontWeight: 700 }}>
                                        <CreditCard size={16} />
                                        <span>Payment Method: {isFirstTime ? "Online Payment Gateway" : "Online / SalarPay Wallet"}</span>
                                    </div>
                                    {isFirstTime && (
                                        <p style={{ margin: "6px 0 0", color: "#14532d", fontSize: "11px", fontWeight: 500 }}>
                                            First-time KYC verification is completed securely via Payment Gateway.
                                        </p>
                                    )}
                                </div>

                                {/* Transaction Password Field */}
                                <div className="user-kyc-modal-card">
                                    <label style={{ display: "block", marginBottom: "6px", fontSize: "12px", fontWeight: 750, color: "#0f172a" }}>
                                        Transaction Password
                                    </label>
                                    <div className="user-kyc-password-wrap">
                                        <input
                                            type={showPassword ? "text" : "password"}
                                            value={form.transactionPassword}
                                            onChange={(e) => updateField("transactionPassword", e.target.value)}
                                            placeholder="Enter your transaction password"
                                            autoComplete="new-password"
                                            className={errors.transactionPassword ? "is-invalid" : ""}
                                        />
                                        <button
                                            type="button"
                                            className="user-kyc-password-toggle"
                                            onClick={() => setShowPassword(!showPassword)}
                                            aria-label={showPassword ? "Hide password" : "Show password"}
                                        >
                                            {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                                        </button>
                                    </div>
                                    {errors.transactionPassword && (
                                        <span className="user-kyc-field-error">
                                            {errors.transactionPassword}
                                        </span>
                                    )}
                                </div>
                            </>
                        )}
                    </div>

                    <div className="user-kyc-modal-footer">
                        <button
                            type="button"
                            className="user-kyc-draft-button"
                            disabled={verifyingState}
                            onClick={() => setShowConfirmModal(false)}
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            className="user-kyc-submit-button"
                            disabled={loadingChargeInfo || verifyingState}
                            onClick={confirmAndSubmitVerification}
                        >
                            {verifyingState ? "Verifying..." : "Verify & Continue"}
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <section className="user-kyc-page">
            <form className="user-kyc-shell" onSubmit={handleSubmit}>
                <div className="user-kyc-page-heading">
                    <div>
                        <span className="user-kyc-kicker">MY ACCOUNT</span>
                        <h1>KYC &amp; Bank Verification</h1>
                        <p>Verify your identity, bank account and nominee details securely.</p>
                    </div>

                    <div className="user-kyc-header-actions">
                        <div
                            className={`user-kyc-status ${statusBadgeClass} ${(activeKycData?.kyc || activeKycData?.bank) ? "clickable" : ""}`}
                            onClick={() => {
                                if (activeKycData?.kyc || activeKycData?.bank) {
                                    setShowResultModal(true);
                                }
                            }}
                            title={(activeKycData?.kyc || activeKycData?.bank) ? "Click to view verification details" : ""}
                        >
                            {isVerified ? (
                                <BadgeCheck size={15} strokeWidth={2} />
                            ) : isRejected ? (
                                <AlertCircle size={15} strokeWidth={2} />
                            ) : (
                                <ShieldCheck size={15} strokeWidth={2} />
                            )}
                            <span>{statusText}</span>
                        </div>

                        {(activeKycData?.kyc || activeKycData?.bank) && (
                            <button
                                type="button"
                                className="user-kyc-details-btn"
                                onClick={() => setShowResultModal(true)}
                            >
                                <ShieldCheck size={14} />
                                View Details
                            </button>
                        )}
                    </div>
                </div>

                {draftSaved && (
                    <div className="user-kyc-message draft">
                        <Save size={16} />
                        Draft saved successfully.
                    </div>
                )}

                {submitted && (
                    <div className="user-kyc-message success">
                        <CheckCircle2 size={17} strokeWidth={2} />
                        Details submitted for verification.
                    </div>
                )}

                {statusMessage && (
                    <div className="user-kyc-message draft">
                        <Check size={16} />
                        {statusMessage}
                    </div>
                )}

                {reduxError && !submitted && !draftSaved && (
                    <div className="user-kyc-message error">
                        <AlertCircle size={16} />
                        {reduxError}
                    </div>
                )}

                {/* =====================================================
                    KYC DETAILS (ID VERIFICATION)
                   ===================================================== */}
                <section className="user-kyc-card">
                    <div className="user-kyc-card-heading">
                        <span className="user-kyc-card-icon">
                            <BadgeCheck size={19} strokeWidth={1.9} />
                        </span>

                        <div>
                            <span>ID VERIFICATION</span>
                            <h2>KYC Details</h2>
                        </div>
                    </div>

                    <div className="user-kyc-form-grid">
                        <div className={`user-kyc-field ${errors.idType ? "has-error" : ""}`}>
                            <label htmlFor="kyc-id-type">Select ID</label>

                            <KycSelect
                                id="kyc-id-type"
                                value={form.idType}
                                options={isIndia ? kycIdOptions : GLOBAL_ID_OPTIONS}
                                placeholder="Select ID"
                                onChange={(value) => updateField("idType", value)}
                            />
                            {errors.idType && (
                                <span className="user-kyc-field-error">{errors.idType}</span>
                            )}
                        </div>

                        <div className={`user-kyc-field ${errors.idNumber ? "has-error" : ""}`}>
                            <label htmlFor="kyc-id-number">ID Number</label>

                            <input
                                id="kyc-id-number"
                                type="text"
                                value={form.idNumber}
                                disabled={!form.idType}
                                onChange={(event) => updateField("idNumber", event.target.value)}
                                placeholder={getIdNumberPlaceholder(form.idType)}
                                className={errors.idNumber ? "is-invalid" : ""}
                            />
                            {errors.idNumber && (
                                <span className="user-kyc-field-error">{errors.idNumber}</span>
                            )}
                        </div>

                        <div className="user-kyc-field user-kyc-field-full">
                            <div className="user-kyc-upload-grid">
                                <div>
                                    <KycImageUpload
                                        id="kyc-front-image"
                                        label="ID Front Image"
                                        value={form.frontImage}
                                        fileName={form.frontImageName}
                                        uploading={uploadingFront}
                                        onSelectFile={(file) => handleFileUpload(file, "front")}
                                        onChange={(imageUrl, fileName) => {
                                            setForm((prev) => ({
                                                ...prev,
                                                frontImage: imageUrl,
                                                frontImageName: fileName,
                                            }));
                                            setSubmitted(false);
                                        }}
                                        onDelete={() => {
                                            updateField("frontImage", "");
                                            updateField("frontImageName", "");
                                        }}
                                    />
                                    {errors.frontImage && (
                                        <span className="user-kyc-field-error">{errors.frontImage}</span>
                                    )}
                                </div>

                                <div>
                                    <KycImageUpload
                                        id="kyc-back-image"
                                        label="ID Back Image"
                                        value={form.backImage}
                                        fileName={form.backImageName}
                                        uploading={uploadingBack}
                                        onSelectFile={(file) => handleFileUpload(file, "back")}
                                        onChange={(imageUrl, fileName) => {
                                            setForm((prev) => ({
                                                ...prev,
                                                backImage: imageUrl,
                                                backImageName: fileName,
                                            }));
                                            setSubmitted(false);
                                        }}
                                        onDelete={() => {
                                            updateField("backImage", "");
                                            updateField("backImageName", "");
                                        }}
                                    />
                                    {errors.backImage && (
                                        <span className="user-kyc-field-error">{errors.backImage}</span>
                                    )}
                                </div>
                            </div>
                        </div>

                        <div className="user-kyc-field user-kyc-field-full">
                            <label htmlFor="kyc-remarks">Remarks</label>

                            <textarea
                                id="kyc-remarks"
                                value={form.remarks}
                                onChange={(event) => updateField("remarks", event.target.value)}
                                placeholder="Add remarks if required"
                            />
                        </div>
                    </div>
                </section>

                {/* =====================================================
                    BANK DETAILS (PAYMENT DETAILS)
                   ===================================================== */}
                <section className="user-kyc-card">
                    <div className="user-kyc-card-heading">
                        <span className="user-kyc-card-icon">
                            <Building2 size={19} strokeWidth={1.9} />
                        </span>

                        <div>
                            <span>PAYMENT DETAILS</span>
                            <h2>Bank Details</h2>
                        </div>
                    </div>

                    <div className="user-kyc-form-grid">
                        <div className="user-kyc-field">
                            <label htmlFor="kyc-country">Country</label>

                            <KycSelect
                                id="kyc-country"
                                value={form.country}
                                options={countryOptions}
                                placeholder="Select Country"
                                onChange={handleCountryChange}
                            />
                        </div>

                        <div className={`user-kyc-field ${errors.bank ? "has-error" : ""}`}>
                            <label htmlFor="kyc-bank">Bank</label>

                            <KycSelect
                                id="kyc-bank"
                                value={form.bank}
                                options={bankOptions}
                                placeholder={form.country ? "Select Bank" : "Select Country First"}
                                disabled={!form.country}
                                onChange={(value) => updateField("bank", value)}
                            />
                            {errors.bank && (
                                <span className="user-kyc-field-error">{errors.bank}</span>
                            )}
                        </div>

                        <div className={`user-kyc-field user-kyc-field-full ${errors.accountHolderName ? "has-error" : ""}`}>
                            <label htmlFor="kyc-account-holder">Account Holder Name</label>

                            <input
                                id="kyc-account-holder"
                                type="text"
                                value={form.accountHolderName}
                                onChange={(event) => updateField("accountHolderName", event.target.value)}
                                placeholder="Enter your name as per account"
                                className={errors.accountHolderName ? "is-invalid" : ""}
                            />
                            {errors.accountHolderName && (
                                <span className="user-kyc-field-error">{errors.accountHolderName}</span>
                            )}
                        </div>

                        <div className={`user-kyc-field ${errors.accountType ? "has-error" : ""}`}>
                            <label htmlFor="kyc-account-type">Account Type</label>

                            <KycSelect
                                id="kyc-account-type"
                                value={form.accountType}
                                options={accountTypeOptions}
                                placeholder="Select Account Type"
                                onChange={(value) => updateField("accountType", value)}
                            />
                            {errors.accountType && (
                                <span className="user-kyc-field-error">{errors.accountType}</span>
                            )}
                        </div>

                        <div className={`user-kyc-field ${errors.accountNumber ? "has-error" : ""}`}>
                            <label htmlFor="kyc-account-number">
                                {isIndia ? "Account Number (9–18 digits)" : "Account Number or IBAN"}
                            </label>

                            <input
                                id="kyc-account-number"
                                type="text"
                                value={form.accountNumber}
                                onChange={(event) => updateField("accountNumber", event.target.value)}
                                placeholder={isIndia ? "Enter Your Account Number" : "Enter Account Number or IBAN"}
                                className={errors.accountNumber ? "is-invalid" : ""}
                            />
                            {errors.accountNumber && (
                                <span className="user-kyc-field-error">{errors.accountNumber}</span>
                            )}
                        </div>

                        <div className={`user-kyc-field ${errors.ifscCode ? "has-error" : ""}`}>
                            <label htmlFor="kyc-ifsc">
                                {isIndia ? "IFSC Code (11 characters)" : "SWIFT / BIC / Routing Code"}
                            </label>

                            <input
                                id="kyc-ifsc"
                                type="text"
                                value={form.ifscCode}
                                onChange={(event) => updateField("ifscCode", event.target.value)}
                                placeholder={isIndia ? "e.g. SBIN0001234" : "e.g. ABCEINBB"}
                                className={errors.ifscCode ? "is-invalid" : ""}
                            />
                            {errors.ifscCode && (
                                <span className="user-kyc-field-error">{errors.ifscCode}</span>
                            )}
                        </div>

                        <div className={`user-kyc-field ${errors.branchName ? "has-error" : ""}`}>
                            <label htmlFor="kyc-branch">
                                {isIndia ? "Branch Name" : "Bank Address / Branch"}
                            </label>

                            <input
                                id="kyc-branch"
                                type="text"
                                value={form.branchName}
                                onChange={(event) => updateField("branchName", event.target.value)}
                                placeholder={isIndia ? "Enter Branch Name" : "Enter Bank Address"}
                                className={errors.branchName ? "is-invalid" : ""}
                            />
                            {errors.branchName && (
                                <span className="user-kyc-field-error">{errors.branchName}</span>
                            )}
                        </div>

                        <div className={`user-kyc-field user-kyc-field-full ${errors.panNumber ? "has-error" : ""}`}>
                            <label htmlFor="kyc-pan">
                                {isIndia ? "PAN Number (Optional, e.g. ABCDE1234F)" : "Tax ID / SSN (Optional)"}
                            </label>

                            <input
                                id="kyc-pan"
                                type="text"
                                value={form.panNumber}
                                onChange={(event) => updateField("panNumber", event.target.value)}
                                placeholder={isIndia ? "Enter PAN Number" : "Enter Tax ID"}
                                className={errors.panNumber ? "is-invalid" : ""}
                            />
                            {errors.panNumber && (
                                <span className="user-kyc-field-error">{errors.panNumber}</span>
                            )}
                        </div>
                    </div>
                </section>

                {/* =====================================================
                    NOMINEE DETAILS (BENEFICIARY)
                   ===================================================== */}
                <section className="user-kyc-card">
                    <div className="user-kyc-card-heading">
                        <span className="user-kyc-card-icon">
                            <UserRound size={19} strokeWidth={1.9} />
                        </span>

                        <div>
                            <span>BENEFICIARY</span>
                            <h2>Nominee Details (Optional)</h2>
                        </div>
                    </div>

                    <div className="user-kyc-form-grid">
                        <div className={`user-kyc-field ${errors.nomineeName ? "has-error" : ""}`}>
                            <label htmlFor="kyc-nominee-name">Nominee Name</label>

                            <input
                                id="kyc-nominee-name"
                                type="text"
                                value={form.nomineeName}
                                onChange={(event) => updateField("nomineeName", event.target.value)}
                                placeholder="Enter Nominee Name"
                                className={errors.nomineeName ? "is-invalid" : ""}
                            />
                            {errors.nomineeName && (
                                <span className="user-kyc-field-error">{errors.nomineeName}</span>
                            )}
                        </div>

                        <div className={`user-kyc-field ${errors.nomineeMobile ? "has-error" : ""}`}>
                            <label htmlFor="kyc-nominee-mobile">Nominee Mobile No</label>

                            <input
                                id="kyc-nominee-mobile"
                                type="tel"
                                value={form.nomineeMobile}
                                onChange={(event) => updateField("nomineeMobile", event.target.value)}
                                placeholder="Enter Nominee Mobile No"
                                className={errors.nomineeMobile ? "is-invalid" : ""}
                            />
                            {errors.nomineeMobile && (
                                <span className="user-kyc-field-error">{errors.nomineeMobile}</span>
                            )}
                        </div>

                        <div className={`user-kyc-field ${errors.nomineeRelation ? "has-error" : ""}`}>
                            <label htmlFor="kyc-nominee-relation">Nominee Relation</label>

                            <input
                                id="kyc-nominee-relation"
                                type="text"
                                value={form.nomineeRelation}
                                onChange={(event) => updateField("nomineeRelation", event.target.value)}
                                placeholder="e.g. Spouse, Father, Mother"
                                className={errors.nomineeRelation ? "is-invalid" : ""}
                            />
                            {errors.nomineeRelation && (
                                <span className="user-kyc-field-error">{errors.nomineeRelation}</span>
                            )}
                        </div>

                        <div className={`user-kyc-field ${errors.nomineeEmail ? "has-error" : ""}`}>
                            <label htmlFor="kyc-nominee-email">Nominee E-mail</label>

                            <input
                                id="kyc-nominee-email"
                                type="email"
                                value={form.nomineeEmail}
                                onChange={(event) => updateField("nomineeEmail", event.target.value)}
                                placeholder="Enter Nominee E-mail"
                                className={errors.nomineeEmail ? "is-invalid" : ""}
                            />
                            {errors.nomineeEmail && (
                                <span className="user-kyc-field-error">{errors.nomineeEmail}</span>
                            )}
                        </div>

                        <div className={`user-kyc-field user-kyc-field-full ${errors.transactionPassword ? "has-error" : ""}`}>
                            <label htmlFor="kyc-transaction-password">Transaction Password</label>

                            <div className="user-kyc-password-wrap">
                                <input
                                    id="kyc-transaction-password"
                                    type={showPassword ? "text" : "password"}
                                    value={form.transactionPassword}
                                    onChange={(event) => updateField("transactionPassword", event.target.value)}
                                    placeholder="Enter Your Transaction Password"
                                    autoComplete="new-password"
                                    className={errors.transactionPassword ? "is-invalid" : ""}
                                />
                                <button
                                    type="button"
                                    className="user-kyc-password-toggle"
                                    onClick={() => setShowPassword(!showPassword)}
                                    aria-label={showPassword ? "Hide password" : "Show password"}
                                >
                                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                                </button>
                            </div>
                            {errors.transactionPassword && (
                                <span className="user-kyc-field-error">{errors.transactionPassword}</span>
                            )}
                        </div>
                    </div>
                </section>

                {/* =====================================================
                    FINAL ACTIONS
                   ===================================================== */}
                <div className="user-kyc-final-actions">
                    <div className="user-kyc-secure-note">
                        <FileCheck2 size={15} strokeWidth={2} />
                        <span>Your documents are used only for account verification.</span>
                    </div>

                    <div className="user-kyc-action-buttons">
                        {/* Draft save is disabled until the Java backend exposes POST /customer/draft
                            (only GET exists today). */}
                        <button
                            type="button"
                            className="user-kyc-draft-button"
                            disabled
                            title="Save Draft is temporarily unavailable"
                        >
                            <Save size={15} strokeWidth={2} />
                            Save Draft
                        </button>

                        <button
                            type="submit"
                            className="user-kyc-submit-button"
                            disabled={loading}
                        >
                            <FileCheck2 size={16} strokeWidth={2} />
                            {loading ? "Processing..." : "Submit for Verification"}
                        </button>
                    </div>
                </div>
            </form>

            {renderCheckoutModal()}
            {renderVerificationResultModal()}
        </section>
    );
};

export default KycPage;
